import { css } from '@emotion/react'
import { Button, HFlow, Text, Theme, useTheme, VFlow } from 'bold-ui'
import { useCallback, useEffect, useRef } from 'react'
import { TooltipRenderProps } from 'react-joyride'

import { useTourTooltipRefContext } from './TourContext'

type TourTooltipProps = TooltipRenderProps

export const TourTooltip = ({
  index,
  step,
  size,
  backProps,
  primaryProps,
  skipProps,
  tooltipProps,
  isLastStep,
}: TourTooltipProps) => {
  const theme = useTheme()
  const styles = createStyles(theme)
  const { setTooltipNode } = useTourTooltipRefContext()
  const tooltipRootRef = useRef<HTMLDivElement | null>(null)

  const handleTooltipRef = useCallback(
    (node: HTMLDivElement | null) => {
      tooltipRootRef.current = node
      setTooltipNode(node)
    },
    [setTooltipNode]
  )

  useEffect(() => {
    const tooltipRoot = tooltipRootRef.current
    if (!tooltipRoot) {
      return
    }

    const primaryButton = tooltipRoot.querySelector<HTMLButtonElement>('[data-testid="TourTooltip.primaryButton"]')
    primaryButton?.focus()
  }, [index, step.target])

  return (
    <div {...tooltipProps} css={styles.tooltip} ref={handleTooltipRef}>
      <VFlow vSpacing={1}>
        <HFlow justifyContent='space-between' alignItems='center'>
          {step.title && (
            <Text variant='h5' weight='bold' style={styles.title}>
              {step.title}
            </Text>
          )}
          <Text style={styles.stepCounter}>
            {index + 1} de {size}
          </Text>
        </HFlow>

        <Text component='div'>{step.content}</Text>

        <div css={styles.actions}>
          <Button skin='ghost' size='small' data-testid='TourTooltip.skipButton' {...skipProps}>
            Fechar
          </Button>
          <HFlow hSpacing={0.5} alignItems='center'>
            {index > 0 && (
              <Button skin='outline' size='small' data-testid='TourTooltip.backButton' {...backProps}>
                Voltar
              </Button>
            )}
            <Button
              kind='primary'
              skin='default'
              size='small'
              data-testid='TourTooltip.primaryButton'
              {...primaryProps}
            >
              {isLastStep ? 'Concluir' : 'Próximo'}
            </Button>
          </HFlow>
        </div>
      </VFlow>
    </div>
  )
}

const createStyles = (theme: Theme) => ({
  tooltip: css`
    background: #fff;
    padding: 1.5rem;
    border-radius: 4px;
    width: 30rem;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  `,
  title: css`
    color: ${theme.pallete.primary.main};
    font-weight: bold;
  `,
  stepCounter: css`
    font-size: 12px;
    white-space: nowrap;
  `,
  actions: css`
    display: flex;
    justify-content: space-between;
    align-items: center;
    width: 100%;
  `,
})
